import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import { TRAVEL_DATE_SELECTION_FORM } from "../../../components/newBniTravelPolicy/travelFlowSteps/dateSelection/dateSelection";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useBniTravelFlow from "../../../hooks/useBniTravelFlow";
import { selectLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import {
  resetTravelPolicyFormData,
  selectTravelPolicyFormData,
} from "../../../redux/features/newTravelPolicyFormSlice";
import { fetchPartyCode } from "../../../redux/features/quoteSlice";
import { resetBniTravelFlow } from "../../../redux/features/bniTravelInsuranceFlowSlice";
import { clearTravelFiles } from "../../../utils/travelFilesStorage";
import { bniTravelStepperData } from "./data";

// Lazy-loaded travel flow step pages — each step is fetched only when the user
// reaches it, keeping the initial bundle small.
const TravelStartPage = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/startPage/startPage")
);
const TravelDateSelection = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/dateSelection/dateSelection")
);
const EditTripInfo = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/dateSelection/EditTripInfo")
);
const TravelOCRResult = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/dateSelection/TravelOCRResult")
);
const TravelPlanChoice = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/planChoice/planChoice")
);
const ContactDetails = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/contactDetails/contactDetails")
);
const CoverageDetails = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/coverageDetails/coverageDetails")
);
const Summary = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/summary/summary")
);
const PaymentMethods = lazy(() =>
  import("../../../components/newBniTravelPolicy/travelFlowSteps/payment/paymentMethods")
);

const NewTravelPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [showModal, setShowModal] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const { currentStep, currentSubstep } = useBniTravelFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const formData = useSelector(selectTravelPolicyFormData);

  const isGuest = !loginTokenInfo;

  useEffect(() => {
    if (loginTokenInfo?.cpr) {
      console.log("🔑 Fetching party code for logged-in user...");
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [dispatch, loginTokenInfo?.cpr]);

  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem(
      "isRedirectingToBniPayment"
    );
    if (wasRedirectingToPayment === "true") {
      console.log(
        "🔓 Clearing BNI payment redirect flag - user returned or component mounted"
      );
      sessionStorage.removeItem("isRedirectingToBniPayment");
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStorage.getItem("isRedirectingToBniPayment") === "true") {
        console.log("⏭️ Skipping BNI flow reset - redirecting to payment");
        return;
      }
      dispatch(resetBniTravelFlow());
      dispatch(resetTravelPolicyFormData());
      clearTravelFiles();
    };

    const handlePopState = () => {
      if (sessionStorage.getItem("isRedirectingToBniPayment") === "true") {
        console.log("⏭️ Skipping BNI flow reset - redirecting to payment");
        return;
      }
      if (!isPaymentProcessing) {
        dispatch(resetBniTravelFlow());
        dispatch(resetTravelPolicyFormData());
        clearTravelFiles();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      if (sessionStorage.getItem("isRedirectingToBniPayment") === "true") {
        console.log(
          "⏭️ Skipping BNI flow reset on unmount - redirecting to payment"
        );
        return;
      }

      dispatch(resetBniTravelFlow());
      dispatch(resetTravelPolicyFormData());
      clearTravelFiles();
    };
  }, [dispatch, isPaymentProcessing]);

  useEffect(() => {
    const currentPath = location.pathname;

    return () => {
      if (sessionStorage.getItem("isRedirectingToBniPayment") === "true") {
        console.log(
          "⏭️ Skipping BNI flow reset on route change - redirecting to payment"
        );
        return;
      }

      if (!currentPath.includes("/new-travel-policy")) {
        dispatch(resetBniTravelFlow());
        dispatch(resetTravelPolicyFormData());
        clearTravelFiles();
      }
    };
  }, [location.pathname]);

  const PageRenderer = () => {
    switch (currentStep?.name) {
      case "START":
        return <TravelStartPage onClose={handleCloseModal} />;
      case "DATE":
        const dateSelectionData = formData[TRAVEL_DATE_SELECTION_FORM] || {};
        const hasFlightTickets = dateSelectionData.hasFlightTickets;

        if (hasFlightTickets === true) {
          if (currentSubstep === "ocr_result") {
            return <TravelOCRResult />;
          }
          if (currentSubstep === "edit_trip_info") {
            return <EditTripInfo />;
          }
        }
        return <TravelDateSelection onClose={handleCloseModal} />;
      case "PLAN":
        return (
          <TravelPlanChoice onClose={handleCloseModal} isGuest={isGuest} />
        );
      case "DETAILS":
        if (currentSubstep === "coverage_selection") {
          return <CoverageDetails />;
        }
        return <ContactDetails isGuest={isGuest} />;
      case "SUMMARY":
        if (currentSubstep === "payment_methods") {
          return <PaymentMethods />;
        }
        return <Summary onPaymentStart={() => setIsPaymentProcessing(true)} />;
      default:
        return null;
    }
  };

  const activeStepData = useMemo(() => {
    let activeStep = bniTravelStepperData.find(
      (each) => each.key === currentStep.name
    );
    return activeStep;
  }, [currentStep]);

  const handleCloseModal = () => {
    dispatch(resetBniTravelFlow());
    dispatch(resetTravelPolicyFormData());
    clearTravelFiles();
    setShowModal(false);
    navigate("/");
  };

  const shouldHideHeader =
    currentStep?.name === "SUMMARY" && currentSubstep === "payment_methods";

  return (
    <Modal show={showModal}>
      {!shouldHideHeader && (
        <Header
          isPopup
          hideMobileClose
          onCloseModal={handleCloseModal}
          showStepper={true}
          steps={bniTravelStepperData}
          activeStep={activeStepData}
          showBackBtn={false}
        />
      )}
      <div
        className={`mainTravelDetailsModal relative ${
          shouldHideHeader ? "pt-0" : "pt-5"
        } pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-0 max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0`}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={`${currentStep?.name || "start"}-${currentSubstep || ""}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-16">
                  <div className="size-8 rounded-full border-2 border-[#00719A] border-t-transparent animate-spin" />
                </div>
              }
            >
              {PageRenderer()}
            </Suspense>
          </motion.div>
        </AnimatePresence>
        {/* Stable mount point for TravelBottomStickyBtnLayout footer.
            Kept outside AnimatePresence so the bottom button never exits/enters between steps. */}
        <div id="travel-footer-portal" />
        <div id="travel-back-portal" />
      </div>
    </Modal>
  );
};

export default NewTravelPolicy;
